/**
 * config.ts — parse data-mp-* attributes into a typed Config object.
 *
 * Supported attributes on any trigger element:
 *   data-mp-org      (required) org slug
 *   data-mp-mode     inline | popup | floating   (default: popup)
 *   data-mp-service  service UUID (optional pre-select)
 *   data-mp-staff    staff UUID (optional pre-select)
 *   data-mp-location location UUID (optional pre-select)
 *   data-mp-color    brand colour hex e.g. "#0ea5e9" (optional)
 *   data-mp-label    floating button label (default: "Book now")
 *   data-mp-position floating button position: bottom-right | bottom-left (default: bottom-right)
 */

export type EmbedMode = 'inline' | 'popup' | 'floating'

export interface EmbedConfig {
  org:       string
  mode:      EmbedMode
  service:   string | null
  staff:     string | null
  location:  string | null
  color:     string | null
  label:     string
  position:  'bottom-right' | 'bottom-left'
  /** Analytics session ID — null if DNT or not yet generated. */
  sessionId: string | null
}

const VALID_MODES: EmbedMode[] = ['inline', 'popup', 'floating']
const VALID_POSITIONS = ['bottom-right', 'bottom-left'] as const

export function parseConfig(el: HTMLElement): EmbedConfig | null {
  const org = el.dataset.mpOrg?.trim()
  if (!org) {
    console.warn('[ManagePatients] Missing data-mp-org attribute — skipping element', el)
    return null
  }

  const rawMode = el.dataset.mpMode?.trim() ?? 'popup'
  const mode: EmbedMode = VALID_MODES.includes(rawMode as EmbedMode)
    ? (rawMode as EmbedMode)
    : 'popup'

  const rawPos = el.dataset.mpPosition?.trim() ?? 'bottom-right'
  const position = VALID_POSITIONS.includes(rawPos as typeof VALID_POSITIONS[number])
    ? (rawPos as typeof VALID_POSITIONS[number])
    : 'bottom-right'

  return {
    org,
    mode,
    service:   el.dataset.mpService?.trim()  || null,
    staff:     el.dataset.mpStaff?.trim()    || null,
    location:  el.dataset.mpLocation?.trim() || null,
    color:     el.dataset.mpColor?.trim()    || null,
    label:     el.dataset.mpLabel?.trim()    || 'Book now',
    position,
    sessionId: null, // populated by index.ts after DNT check
  }
}

/**
 * Build the iframe src URL from a config object.
 * Passes query params for pre-selects + context.
 * UTM params are forwarded from the host page URL if present.
 */
export function buildIframeSrc(config: EmbedConfig, cdn: string): string {
  const url = new URL(`${cdn}/embed/book/${encodeURIComponent(config.org)}`)

  if (config.service)  url.searchParams.set('service',  config.service)
  if (config.staff)    url.searchParams.set('staff',    config.staff)
  if (config.location) url.searchParams.set('location', config.location)
  if (config.color)     url.searchParams.set('color',     config.color)
  if (config.sessionId) url.searchParams.set('sessionId', config.sessionId)

  // Always mark as embed so the page can adapt UI if needed
  url.searchParams.set('mode', 'embed')

  // Forward UTM params from the host page
  const hostParams = new URLSearchParams(window.location.search)
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
    const val = hostParams.get(key)
    if (val) url.searchParams.set(key, val)
  }

  return url.toString()
}
